(function () {
  'use strict';

  // ============================================================
  // Google Merchant Center Automated Discounts - Price Honoring
  // Reads pv2 JWT from URL, decodes payload, applies discounted
  // price on Product Page, Cart, and Checkout (Shopify Plus).
  // ============================================================

  var GPV2 = {

    // --- Config ---
    PRICE_ATTR: 'data-product-price',
    COMPARE_ATTR: 'data-compare-price',
    CONTAINER_ATTR: 'data-product-price-wrap',
    SAVE_ATTR: 'data-save-price',
    STORAGE_KEY: 'gpv2_discounts',
    STORAGE_TTL: 30 * 60 * 1000, // 30 minutes

    // --- Init ---
    init: function () {
      var token = this.getTokenFromURL();
      if (token) {
        var payload = this.decodeJWT(token);
        if (payload) {
          this.storeDiscounts(payload);
        }
      }
      this.applyDiscounts();
    },

    // --- Extract pv2 from URL ---
    getTokenFromURL: function () {
      try {
        var params = new URLSearchParams(window.location.search);
        return params.get('pv2') || null;
      } catch (e) {
        return null;
      }
    },

    // --- Decode JWT (Base64 payload only, no signature verification needed) ---
    decodeJWT: function (token) {
      try {
        var parts = token.split('.');
        if (parts.length < 2) return null;
        var base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        var pad = base64.length % 4;
        if (pad) { base64 += '==='.slice(0, 4 - pad); }
        var json = decodeURIComponent(
          atob(base64).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          }).join('')
        );
        return JSON.parse(json);
      } catch (e) {
        console.warn('[GPV2] JWT decode failed:', e);
        return null;
      }
    },

    // --- Store discounts in sessionStorage with TTL ---
    storeDiscounts: function (payload) {
      try {
        var data = {
          payload: payload,
          expires: Date.now() + this.STORAGE_TTL
        };
        sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
      } catch (e) {}
    },

    // --- Load valid discounts ---
    loadDiscounts: function () {
      try {
        var raw = sessionStorage.getItem(this.STORAGE_KEY);
        if (!raw) return null;
        var data = JSON.parse(raw);
        if (Date.now() > data.expires) {
          sessionStorage.removeItem(this.STORAGE_KEY);
          return null;
        }
        return data.payload;
      } catch (e) {
        return null;
      }
    },

    // --- Get discounted price for a variant (in cents) ---
    // Google pv2 payload can be:
    //   Flat:   { price: 19.99, product_id: '...', variant_id: '...' }
    //   Nested: { products: [ { id: '...', variants: [ { id: '...', price: 19.99 } ] } ] }
    getDiscountedPrice: function (payload, productId, variantId) {
      if (!payload) return null;

      // Flat structure
      if (typeof payload.price !== 'undefined') {
        return Math.round(Number(payload.price) * 100);
      }

      // Nested products array
      if (Array.isArray(payload.products)) {
        for (var i = 0; i < payload.products.length; i++) {
          var p = payload.products[i];
          if (productId && p.id && String(p.id) !== String(productId)) continue;
          if (Array.isArray(p.variants)) {
            for (var j = 0; j < p.variants.length; j++) {
              var v = p.variants[j];
              if (!variantId || !v.id || String(v.id) === String(variantId)) {
                return Math.round(Number(v.price) * 100);
              }
            }
          } else if (typeof p.price !== 'undefined') {
            return Math.round(Number(p.price) * 100);
          }
        }
      }

      return null;
    },

    // --- Format price (cents → $XX.XX) ---
    formatMoney: function (cents) {
      var amount = (cents / 100).toFixed(2);
      return '$' + amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },

    parseMoneyToCents: function (str) {
      if (!str) return 0;
      var num = parseFloat(str.replace(/[^0-9.]/g, ''));
      return Math.round(num * 100);
    },

    // --- Apply discounts to all price elements on page ---
    applyDiscounts: function () {
      var self = this;
      var payload = this.loadDiscounts();
      if (!payload) return;

      // Product Page
      this.applyProductPageDiscount(payload);

      // Cart Page
      this.applyCartPageDiscount(payload);

      // Watch for variant changes (Expanse theme fires 'variantChange')
      document.addEventListener('variantChange', function (e) {
        if (e.detail && e.detail.variant) {
          self.applyProductPageDiscountForVariant(payload, e.detail.variant);
        }
      });

      // Also handle generic 'variant:change'
      document.addEventListener('variant:change', function (e) {
        if (e.detail && e.detail.variant) {
          self.applyProductPageDiscountForVariant(payload, e.detail.variant);
        }
      });
    },

    // --- Product page price update ---
    applyProductPageDiscount: function (payload) {
      var priceEl = document.querySelector('[data-product-price]');
      if (!priceEl) return;

      var productSection = document.querySelector('[data-section-type="product"]');
      var productId = productSection ? productSection.getAttribute('data-product-id') : null;

      var variantSelect = document.querySelector('[data-product-select]');
      var variantId = variantSelect ? variantSelect.value : null;

      this.applyProductPageDiscountForVariant(payload, { id: variantId, product_id: productId });
    },

    applyProductPageDiscountForVariant: function (payload, variant) {
      if (!variant) return;
      var variantId = variant.id;
      var productId = variant.product_id;

      if (!productId) {
        var productSection = document.querySelector('[data-section-type="product"]');
        productId = productSection ? productSection.getAttribute('data-product-id') : null;
      }

      var discountedCents = this.getDiscountedPrice(payload, productId, variantId);
      if (discountedCents === null) return;

      var priceEl = document.querySelector('[data-product-price]');
      if (!priceEl) return;

      // Save original price before first modification
      if (!priceEl.hasAttribute('data-gpv2-applied')) {
        var origSpanText = (priceEl.querySelector('[aria-hidden="true"]') || priceEl).textContent.trim();
        priceEl.setAttribute('data-original-price', origSpanText);
        priceEl.setAttribute('data-gpv2-applied', '1');
      }

      var discountedFormatted = this.formatMoney(discountedCents);

      // Update displayed sale price
      var span = priceEl.querySelector('[aria-hidden="true"]');
      if (span) span.textContent = discountedFormatted;
      else priceEl.textContent = discountedFormatted;

      priceEl.classList.add('on-sale', 'gpv2-price');

      // Show compare-at (original) price
      var compareWrap = document.querySelector('[data-product-price-wrap]');
      var compareEl = document.querySelector('[data-compare-price]');

      if (compareWrap) compareWrap.classList.remove('hide');
      if (compareEl) {
        var cSpan = compareEl.querySelector('[aria-hidden="true"]') || compareEl;
        cSpan.textContent = priceEl.getAttribute('data-original-price');
      }

      // Update save badge
      this.updateSaveBadge(priceEl.getAttribute('data-original-price'), discountedCents);

      // Inject Google discount banner
      this.injectGPV2Banner(discountedFormatted);
    },

    updateSaveBadge: function (originalText, discountedCents) {
      var saveEl = document.querySelector('[data-save-price]');
      if (!saveEl) return;
      var originalCents = this.parseMoneyToCents(originalText);
      if (!originalCents || originalCents <= discountedCents) return;
      var saved = originalCents - discountedCents;
      var pct = Math.round(saved / originalCents * 100);
      saveEl.textContent = 'Save ' + pct + '%';
      saveEl.classList.remove('hide');
    },

    // --- Visual Google discount badge ---
    injectGPV2Banner: function (price) {
      if (document.getElementById('gpv2-banner')) {
        document.getElementById('gpv2-banner').querySelector('strong').textContent = price;
        return;
      }
      var priceBlock = document.querySelector('.product-block--price');
      if (!priceBlock) return;
      var banner = document.createElement('div');
      banner.id = 'gpv2-banner';
      banner.setAttribute('data-gpv2-price-display', price);
      banner.style.cssText = 'background:#e8f5e9;border:1px solid #34a853;border-radius:4px;padding:8px 12px;margin-top:10px;font-size:13px;color:#1e8e3e;display:flex;align-items:center;gap:8px;';
      banner.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="#34a853" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
        '<span>Google exclusive price: <strong>' + price + '</strong></span>';
      priceBlock.appendChild(banner);
    },

    // --- Cart page ---
    applyCartPageDiscount: function (payload) {
      if (!payload) return;
      this.injectCartNotice();
    },

    injectCartNotice: function () {
      if (document.getElementById('gpv2-cart-notice')) return;
      var target = document.querySelector('.cart-template') ||
                   document.querySelector('#CartContainer') ||
                   document.querySelector('form[action="/cart"]');
      if (!target) return;
      var notice = document.createElement('div');
      notice.id = 'gpv2-cart-notice';
      notice.style.cssText = 'background:#e8f5e9;border:1px solid #34a853;border-radius:4px;padding:10px 14px;margin-bottom:16px;font-size:14px;color:#1e8e3e;';
      notice.innerHTML = '✓ <strong>Google exclusive discount is active.</strong> Your special price will apply at checkout.';
      target.insertBefore(notice, target.firstChild);
    }
  };

  // --- Boot ---
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { GPV2.init(); });
  } else {
    GPV2.init();
  }

  window.GPV2 = GPV2;

})();